import { DashboardFilterValues, QueuePage, QueueFilterValues } from './types';

// Query keys for the Dashboard V2 React Query hooks. Scoped under the
// `'dashboard_v2'` root so a single
// `queryClient.invalidateQueries({ queryKey: ['dashboard_v2'] })` flushes
// every dashboard query. Sub-keys follow `[root, resource, ...inputs]` so
// changing any input rotates the cache slot.

const ROOT = 'dashboard_v2' as const;

export const dashboardV2QueryKeys = {
  root: [ROOT] as const,
  calendarWeek: (
    agencyId: number,
    startDate: string,
    timeZone: string,
    filters: DashboardFilterValues,
    days = 7,
  ) =>
    [
      ROOT,
      'calendar_week',
      agencyId,
      startDate,
      timeZone,
      filters,
      days,
    ] as const,
  summary: (agencyId: number) => [ROOT, 'summary', agencyId] as const,
  filters: (agencyId: number) => [ROOT, 'filters', agencyId] as const,
  // Queue filters are part of the key, so changing a selection rotates the
  // cache slot instead of serving the previous queue's rows (GRAVY-10303).
  //
  // So is the page, for the same reason: each page is its own server response
  // (GRAVY-10304). Keeping them as separate slots also means paging back to a
  // page already visited renders from cache instead of refetching.
  noShow: (agencyId: number, filters: QueueFilterValues, page: QueuePage) =>
    [ROOT, 'no_show', agencyId, filters, page] as const,
  missingTimesheet: (
    agencyId: number,
    filters: QueueFilterValues,
    page: QueuePage,
  ) => [ROOT, 'missing_timesheet', agencyId, filters, page] as const,
  needsApproval: (
    agencyId: number,
    filters: QueueFilterValues,
    page: QueuePage,
  ) => [ROOT, 'needs_approval', agencyId, filters, page] as const,
  shiftDetail: (agencyId: number, shiftId: number) =>
    [ROOT, 'shift_detail', agencyId, shiftId] as const,
  // shiftId is part of the key, so opening the modal on another shift reads a
  // different cache slot instead of showing the previous shift's history while
  // the new request is in flight (GRAVY-10294).
  // The order is in the key too (GRAVY-10877): oldest-first and newest-first
  // are different pages, and sharing a slot would show one under the other.
  shiftActivity: (
    agencyId: number,
    shiftId: number,
    order: 'asc' | 'desc' = 'desc',
  ) => [ROOT, 'shift_activity', agencyId, shiftId, order] as const,
  // The toast feed (GRAVY-10443). One slot per agency: the cursor that pages
  // it is client state held by the hook, not part of the key, so a poll reuses
  // the slot instead of minting a new one every 30 seconds.
  alerts: (agencyId: number) => [ROOT, 'alerts', agencyId] as const,
  // Invite backups (GRAVY-10898). Keyed on the JOB, which is what the endpoint
  // takes; under the root so a send's root invalidation refreshes it too.
  backupInvites: (jobId: number) => [ROOT, 'backup_invites', jobId] as const,
  // The GLOBAL backup-invites switch: the header's "Auto backups on" pill and
  // the Invite backups modal's switch read and flip the same value.
  backupSwitch: () => [ROOT, 'backup_switch'] as const,
};
